import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { last, isEmpty, isNumber, isEqual, uniqBy } from 'lodash';
import withTranslation from 'hoc/withTranslation';
import Button from 'components/Form/Button';
import { PANELS } from 'reducers/user';
import { StyledCloseButton } from 'components/FloatingPanels/Menu/Menu';
import {
  recordUsageEvent,
  selectTourById,
  setOrders,
  updateOrder,
  setSolutionJson,
  setTerritoriesFromProblemFile,
  setTourParameter,
  setUserParam,
  USAGE_EVENTS,
  addReload,
  addPudo as addPudoAction,
  movePudoGroup,
  addParking,
} from 'actions';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styled from '@emotion/styled';
import { getLocationAddress } from 'utils/GeoCoder';
import { v4 } from 'uuid';
import { EXPORT_TYPES } from 'components/Wizard/Step2/Orders';
import {
  generateAvoidAreasFromFileProblem,
  generateExcludeAreasFromFileProblem,
  generatePolygonsFromOrders,
} from 'utils/territories/TerritoriesFromJson';
import { isIterationSolution } from 'utils/SolutionHelpers';
import {
  applyCapacityToFirstVehicle,
  calcInitialReloadCapacity,
  calcReloadCapacity,
  calcTotalDemand,
} from 'utils/reloadHelpers';
import { parseEnabledList } from 'utils/helpers';
import { getVRPTimes } from 'utils/VRPConverterTourPlanner';
import { getPudoFilteredGroups } from 'utils/map/MarkerDragHandler';
import { findNearestOrder } from 'utils/OrdersHelpers';
import H from '@here/maps-api-for-javascript';
import config from '../../config';
import ChevronDownIcon from '../../global/img/chevron-down.svg';

const DROPDOWN_CONSTANTS = {
  WIDTH: '9.8rem',
  LIST_WIDTH: '9.75rem',
  MAX_HEIGHT: '8.5rem',
  TOP_OFFSET: '2.5rem',
  SCROLL_THRESHOLD: 5,
};

const COLORS = {
  BORDER: '#ccc',
  BACKGROUND_WHITE: '#fff',
  BACKGROUND_HOVER: '#e6f7ff',
  TEXT_GRAY: '#888',
  TEXT_DARK: '#333',
  ERROR_RED: 'red',
};

const commonInputStyles = `
  height: 2.25rem;  
  line-height: 1.5rem;
  font-size: 0.875rem;
  box-sizing: border-box;
`;

const StyledLink = styled.li({
  fontSize: '14px',
  cursor: 'pointer',
  color: 'black',
  ':hover': {
    backgroundColor: '#D3D3D3',
  },
});

const StyledName = styled.span({
  display: 'block',
  color: 'black',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '14rem',
});

const StyledList = styled.ul({
  paddingLeft: '15px',
});

const StyledContainer = styled.div`
  margin: 0.625rem;
`;

const StyledLabel = styled.label`
  width: 6rem;
  margin-right: 1rem;
  text-align: left;
  font-size: 0.875rem;
  color: #333;
  white-space: nowrap;
`;

const StyledSelect = styled.select`
  ${commonInputStyles};
  padding: 0.375rem;
  width: ${DROPDOWN_CONSTANTS.WIDTH};
  margin-top: 0.1875rem;
  margin-bottom: 0.375rem;
  font-size: 0.8125rem;
  margin-left: ${(props) => (props.marginLeft ? '0.4rem' : props.marginLeftTwo ? '-0.4rem' : '0')};
  position: ${(props) => props.leftSide && 'relative'};
  left: ${(props) => props.leftSide && '-0.1rem'};
`;

const StyledInput = styled.input`
  ${commonInputStyles};
  padding: 0.2rem;
  font-size: 0.8125rem;
  margin-top: 0.375rem;
  margin-bottom: 0.375rem;
  display: block;
  border: ${(props) => (props.errorObj ? `1px solid ${COLORS.ERROR_RED}` : '1px solid #ccc')};
  width: 10rem;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: ${(props) => props.paddingTop && '0.6rem'};
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  text-align: center;
  align-items: center;
  margin-top: 0.5rem;
`;

const StyledPane = styled.div`
  position: absolute;
  background: rgba(255, 255, 255, 0.8);
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.2);
  z-index: 650;
  pointer-events: auto;
`;

const StyledDropText = styled.span({
  fontWeight: '700',
  height: '1.75rem',
  fontSize: '0.875rem',
  color: '#333',
  marginRight: '0.5rem',
  span: {
    color: '#00afaa',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

const StyledTerrWrapper = styled.div`
  position: relative;
  width: 9.4rem;
`;

const StyledPriorityWrapper = styled.div`
  padding-top: 0.5rem;
`;

const StyledRelativeWrapper = styled.div`
  position: relative;
`;

const StyledInputWithIcon = styled(StyledInput)`
  margin-bottom: 0.3rem;
  width: ${DROPDOWN_CONSTANTS.WIDTH};
  padding-right: 1.5rem;
`;

const StyledIconWrapper = styled.span`
  position: absolute;
  right: -0.2rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 1rem;
  color: ${COLORS.TEXT_GRAY};
  font-weight: 700;
  letter-spacing: 0.1em;
`;

const StyledIcon = styled.img`
  display: inline-block;
  color: ${COLORS.TEXT_GRAY};
  vertical-align: middle;
`;

const StyledDropdownList = styled.ul`
  position: absolute;
  top: ${DROPDOWN_CONSTANTS.TOP_OFFSET};
  left: 0;
  width: ${DROPDOWN_CONSTANTS.LIST_WIDTH};
  max-height: ${DROPDOWN_CONSTANTS.MAX_HEIGHT};
  overflow-y: ${(props) => (props.hasScroll ? 'auto' : 'visible')};
  background: ${COLORS.BACKGROUND_WHITE};
  border: 1px solid ${COLORS.BORDER};
  z-index: 1000;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const StyledDropdownItem = styled.li`
  padding: 0;
  margin: 0;
`;

const StyledDropdownButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.375rem;
  cursor: pointer;
  background: ${(props) =>
    props.isHovered || props.isSelected ? COLORS.BACKGROUND_HOVER : COLORS.BACKGROUND_WHITE};
  border: none;
  text-align: left;
  transition: background 0.2s;
`;

const StyledTerritorySelector = styled.div`
  height: 1.5rem;
  line-height: 1.5rem;
  font-size: 0.875rem;
  width: 9rem;
  border: 1px solid ${COLORS.BORDER};
  background: ${COLORS.BACKGROUND_WHITE};
  cursor: pointer;
  padding: 0.375rem;
  user-select: none;
  position: relative;
`;

const StyledPositionedPane = styled(StyledPane)`
  left: ${(props) => props.x}px;
  top: ${(props) => props.y}px;
  cursor: ${(props) => (props.isDragging ? 'grabbing' : props.isDraggable ? 'grab' : 'auto')};
`;

export const CATEGORY_TYPES = {
  TOUR: 'tour',
  SUB_TOUR: 'subTour',
  NONE: 'None',
};

const getUniqueByName = (items) => {
  if (!items || !Array.isArray(items)) return [];
  return Array.from(
    new Map(items.filter((item) => item?.name).map((item) => [item.name, item])).values(),
  );
};

const getUniqueValues = (items) => {
  if (!items || !Array.isArray(items)) return [];
  return Array.from(new Set(items.filter(Boolean)));
};

const { emptyOrder, dateTimeFormat } = config;

const MAX_RELOADS = 5;
const MAX_BREAKS = 5;

const HereLocationSelector = ({
  map,
  oAuth,
  allowRightClick,
  translations: {
    locationPopup: {
      copyToClipboard,
      addPickup,
      addDelivery,
      addBreak,
      addPudo: addPudoLabel,
      pudoDurationLabel,
      pudoAssignAtFirst,
      pudoAssignAtLast,
      pudoIdLabel,
      addReload: addReloadLabel,
      reloadDuration,
      addParking: addParkingLabel,
      parkingDuration,
      infoEnable,
      infoEnableSettings,
    },
    wizard: {
      orders: {
        orderAny,
        orderNone,
        orderOrdered,
        orderFirst,
        orderLast,
        selectPosition,
        selectCategory,
        selectPriority,
        selectGroup,
        selectMaxTime,
        selectTerritory,
        selectSkills,
        terrPlaceholder,
        skillsPlaceholder,
      },
    },
  },
  display,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef();
  const paneRef = useRef();
  const suggestionsListRef = useRef();
  const skillsSuggestionsListRef = useRef();
  const [visible, setVisible] = useState(true);
  const mapData = useSelector(({ mapData: stateMapData }) => stateMapData);
  const solution = useSelector(({ solution: stateSolution }) => stateSolution);
  const orders = useSelector((state) => state.orders[solution.show]);
  const user = useSelector(({ user: stateUser }) => stateUser);
  const mapTarget = user.mapTarget;
  const userRef = useRef(user);
  const oAuthRef = useRef(oAuth);

  useEffect(() => {
    userRef.current = user;
    oAuthRef.current = oAuth;
  }, [user, oAuth]);

  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[solution.show],
  );
  const terr = useSelector(({ areas: stateAreas }) => stateAreas[0].territories);
  const reloads = useSelector((s) => s.reloads);

  const handleRecordUsage = useCallback((data) => dispatch(recordUsageEvent(data)), [dispatch]);
  const handleSetOrders = useCallback(
    (newOrders) => dispatch(setOrders(newOrders, false, solution.show)),
    [dispatch, solution.show],
  );
  const handleSelectTourById = useCallback((data) => dispatch(selectTourById(data)), [dispatch]);
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);
  const handleSetSolutionJson = useCallback(
    (param) => dispatch(setSolutionJson(param)),
    [dispatch],
  );
  const handleSetTourParameter = useCallback(
    (parameter) => dispatch(setTourParameter(parameter)),
    [dispatch],
  );

  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState('');
  const [boxOrders, setBoxOrders] = useState();
  const [selectedPosition, setSelectedPosition] = useState('none');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedCategory, setselectedCategory] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [focus, setFocus] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [maxTimeOnVehicle, setMaxTimeOnVehicle] = useState('');
  const [territoryName, setTerritoryName] = useState('');
  const [skillsName, setSkillsName] = useState('');
  const [orderNumberError, setOrderNumberError] = useState(false);
  const [recalculateGroups, setRecalculateGroups] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [panePosition, setPanePosition] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [pudoDuration, setPudoDuration] = useState('300');
  const [pudoAssignAt, setPudoAssignAt] = useState('first');
  const [pudoCustomId, setPudoCustomId] = useState('');
  const [showPudoIdList, setShowPudoIdList] = useState(false);
  const [reloadDurationValue, setReloadDurationValue] = useState('900');
  const reloadLimitReached = reloads.length >= MAX_RELOADS;
  const [parkingDurationValue, setParkingDurationValue] = useState('300');
  const breakLimitReached = (tourPlanner.breaks?.length || 0) >= MAX_BREAKS;
  const [showSuggestions, setShowSuggestions] = useState(false);
  const DEFAULT_BREAK_DURATION_MIN = 30;
  const [breakDurationInput, setBreakDurationInput] = useState(DEFAULT_BREAK_DURATION_MIN);
  const [showSkillsSuggestions, setShowSkillsSuggestions] = useState(false);
  const [showTerritoryList, setShowTerritoryList] = useState(false);
  const [showGroupList, setShowGroupList] = useState(false);
  const [showMaxTimeOnVehicleList, setShowMaxTimeOnVehicleList] = useState(false);

  const zoomUnassigned = user?.zoomUnassigned;
  const lastFileObj = last(solution.requests[solution.show].iterations)?.request.fileObj?.fleet
    ?.types;
  const currentJson = user?.currentJson?.fleet?.types;
  const skillsObj = user.mapSettings?.skills;
  const testSkills = parseEnabledList(skillsObj);
  const allSkills = [
    ...((currentJson || lastFileObj)?.flatMap((vehicle) => ({ name: vehicle.skills?.[0] })) || []),
    ...testSkills,
  ];

  const skills = getUniqueByName(allSkills);

  const categoriesSimple = (currentJson || lastFileObj)?.flatMap((vehicle) =>
    (vehicle.shifts || [])?.flatMap((shift) => {
      const level = shift.mixingRestrictions?.level;
      return (shift.mixingRestrictions?.restrictions || []).flatMap((restriction) =>
        (restriction.conflictingCategories || []).map((name) => ({ name, level })),
      );
    }),
  );
  const testCategories = parseEnabledList(user.mapSettings?.categories);

  const uniqueCategories = getUniqueByName(
    testCategories.length > 0 ? testCategories : categoriesSimple || [],
  );

  const [lastValid, setLastValid] = useState(false);
  const [hoveredTerritory, setHoveredTerritory] = useState(null);
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [hoveredMaxTime, setHoveredMaxTime] = useState(null);

  const territ = user.mapSettings?.territories;
  const testTerritories = parseEnabledList(territ);
  const areaNames = new Set((terr?.areaDetails || []).map((t) => t?.name));
  const groupsList = orders.map((o) => o.Group).filter(Boolean);
  const filteredGroups = getUniqueValues(groupsList);
  const pudoFilteredGroups = getPudoFilteredGroups(
    filteredGroups,
    tourPlanner.groups,
    boxOrders,
    orders,
  );
  const maxTimeList = orders.map((o) => o.MaxTime).filter(Boolean);
  const filteredMaxTime = getUniqueValues(maxTimeList);

  const allAreas = [
    ...(terr?.areaDetails?.map((t) => ({ name: t?.name })) || []),
    ...testTerritories.filter((test) => !areaNames.has(test.name)),
  ];

  const filteredSkills = getUniqueByName(skills);
  const filteredAreas = getUniqueByName(allAreas);

  const fleetEnabled = user.fleetEnabled;
  const planEnabled = user.planEnabled;
  const isPositionHidden = !user.mapSettings?.hidePositions || !planEnabled;
  const isGroupHidden = !user.mapSettings?.hideGroups || !planEnabled;
  const isMaxTimeHidden = !user.mapSettings?.hideMaxTimeOnVehicle || !planEnabled;
  const isTerritoriesHidden = !user.mapSettings?.territories?.enabled || !fleetEnabled;
  const isSkillsHidden = !user.mapSettings?.skills?.enabled || !fleetEnabled;
  const isPriorityHidden = !user.mapSettings?.hidePriority || !planEnabled;
  const isCategoryHidden = !user.mapSettings?.categories?.enabled || !fleetEnabled;

  const isSolution =
    solution && isIterationSolution(last(solution.requests[solution.show].iterations));

  const onCopyToClipboard = () => {
    navigator.clipboard.writeText(address);
    toast('Copied Address to clipboard!', {
      autoClose: 1000,
      hideProgressBar: true,
      closeButton: false,
    });
    setPosition(null);
  };

  const handlePositionChange = (e) => {
    const value = e.target.value;
    setSelectedPosition(value);

    if (value === EXPORT_TYPES.ORDER) {
      setFocus(true);
    } else {
      setOrderNumber('');
    }
  };

  const handlePriorityChange = (e) => {
    const value = e.target.value;
    setSelectedPriority(value);
  };

  const onOpenClick = () => {
    handleSetUserParam({ openPanel: PANELS.MAP_SETTINGS });
  };

  const onSubmitValues = () => {
    const updatedOrders = orders.map((item) => {
      if (!boxOrders.includes(item.InternalID)) return item;

      const updatedItem = { ...item };

      if (!isPriorityHidden) {
        const priorityStr = String(selectedPriority).trim();
        if (priorityStr) {
          const numericPriority = Number(priorityStr);
          if (!Number.isNaN(numericPriority)) {
            updatedItem.Priority = numericPriority;
          } else {
            delete updatedItem.Priority;
          }
        } else {
          delete updatedItem.Priority;
        }
      }

      if (!isMaxTimeHidden) {
        const trimmedMaxTime = String(maxTimeOnVehicle || '').trim();
        if (trimmedMaxTime && trimmedMaxTime?.toLowerCase() !== 'none') {
          const num = Number(trimmedMaxTime.replace(',', '.'));
          if (!Number.isNaN(num) && num >= 0) {
            updatedItem.MaxTime = num;
          }
        } else {
          delete updatedItem.MaxTime;
        }
      }

      if (!isGroupHidden) {
        const trimmedGroup = groupName.trim();
        if (trimmedGroup && trimmedGroup.toLowerCase() !== 'none') {
          updatedItem.Group = trimmedGroup;
        } else {
          delete updatedItem.Group;
        }
      }

      if (!isSkillsHidden) {
        const trimmedSkills = skillsName.trim();
        if (trimmedSkills) {
          updatedItem.Skills = trimmedSkills;
        } else {
          delete updatedItem.Skills;
        }
      }

      if (!isTerritoriesHidden) {
        const trimmedTerritory = territoryName.trim();
        if (trimmedTerritory) {
          updatedItem.Territories = [trimmedTerritory];
        } else {
          delete updatedItem.Territories;
        }
      }

      if (!isCategoryHidden) {
        if (categoryName.trim() && categoryName !== CATEGORY_TYPES.NONE) {
          updatedItem.Category = categoryName.trim();
          updatedItem.CategoryType = selectedCategory;
        } else {
          delete updatedItem.Category;
        }
      }

      if (!isPositionHidden) {
        if (selectedPosition === EXPORT_TYPES.NONE) {
          delete updatedItem.Position;
          delete updatedItem.PositionValue;
        } else if (selectedPosition === EXPORT_TYPES.ORDER && orderNumber) {
          const orderNum = Number(orderNumber);
          if (!Number.isNaN(orderNum)) {
            updatedItem.Position = orderNum;
            updatedItem.PositionValue = orderNum;
          } else {
            delete updatedItem.Position;
            delete updatedItem.PositionValue;
          }
        } else {
          updatedItem.Position = selectedPosition;
        }
      }

      return updatedItem;
    });

    const skillTerritoryMap = {};
    const skillGroupMap = {};

    updatedOrders.forEach(({ Skills, Territories, Group }) => {
      if (Skills) {
        const skillList = Array.isArray(Skills)
          ? Skills
          : String(Skills)
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);

        if (Territories) {
          Territories.forEach((territory) => {
            skillList.forEach((skill) => {
              if (!skillTerritoryMap[skill]) {
                skillTerritoryMap[skill] = new Set();
              }
              skillTerritoryMap[skill].add(territory);
            });
          });
        }

        if (Group) {
          skillList.forEach((skill) => {
            if (!skillGroupMap[skill]) {
              skillGroupMap[skill] = new Set();
            }
            skillGroupMap[skill].add(Group);
          });
        }
      }
    });

    const skillTerritoryAssociations = {};
    Object.entries(skillTerritoryMap).forEach(([skill, set]) => {
      skillTerritoryAssociations[skill] = Array.from(set);
    });

    const skillGroupAssociations = {};
    Object.entries(skillGroupMap).forEach(([skill, set]) => {
      skillGroupAssociations[skill] = Array.from(set);
    });

    handleSetTourParameter({ skillTerritoryAssociations, index: solution.show });
    handleSetTourParameter({ skillGroupAssociations, index: solution.show });

    handleSetOrders(uniqBy(updatedOrders, 'InternalID'));

    if (boxOrders && !isGroupHidden) {
      const pudoUpdates = updatedOrders.filter(
        (o) => boxOrders.includes(o.InternalID) && o.Activity === 'pudo',
      );
      if (pudoUpdates.length > 0) {
        pudoUpdates.forEach((pudoOrder) => {
          const newGroupId = pudoOrder.Group;
          if (newGroupId) dispatch(movePudoGroup({ pudoId: pudoOrder.InternalID, newGroupId }));
        });
      }
    }

    handleSetSolutionJson({ jsonTriggerVRP: true, jsonManual: true });

    if (!isGroupHidden || !isTerritoriesHidden || !isMaxTimeHidden) {
      setRecalculateGroups(true);
    }

    setBoxOrders(null);
    setGroupName('');
    setMaxTimeOnVehicle('');
    setOrderNumber('');
    setTerritoryName('');
    setSkillsName('');
    setCategoryName('');
    setSelectedPosition(EXPORT_TYPES.ANY);
    setSelectedPriority('');
  };

  const onValueBlur = useCallback(
    (e) => {
      if (e.target.value === '' && selectedPosition === EXPORT_TYPES.ORDER) {
        setOrderNumber(lastValid || 1);
        if (isEmpty(lastValid)) setOrderNumberError(true);
      } else {
        setOrderNumberError(false);
      }
    },
    [selectedPosition, lastValid],
  );

  const onAddAddress = useCallback(
    (type) => {
      const VEHICLE_TYPES = ['break'];
      if (VEHICLE_TYPES.includes(type)) {
        if (type === 'break' && breakLimitReached) {
          setPosition(null);
          return;
        }
        const loc = { lat: position.lat, lng: position.lng };
        if (type === 'break') {
          const existing = Array.isArray(tourPlanner.breaks) ? tourPlanner.breaks : [];
          const vrpTimes = getVRPTimes(tourPlanner);
          const BREAK_DURATION_SECONDS =
            (parseInt(breakDurationInput, 10) || DEFAULT_BREAK_DURATION_MIN) * 60;
          // Stagger each new break's window so multiple breaks never overlap: each window is
          // 1 hour long with a 30-minute gap before the next one starts (2h-3h, 3:30h-4:30h, ...).
          const breakWindowStart = vrpTimes.start
            .clone()
            .add(2 * 60 + existing.length * 90, 'minutes');
          const breakWindowEnd = breakWindowStart.clone().add(1, 'hour');
          const breakTimes = [
            [breakWindowStart.format(dateTimeFormat), breakWindowEnd.format(dateTimeFormat)],
          ];
          handleSetTourParameter({
            breaks: [
              ...existing,
              {
                location: loc,
                duration: BREAK_DURATION_SECONDS,
                times: breakTimes,
                address,
                isManual: true,
              },
            ],
            index: solution.show,
          });
        }
        handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
        setPosition(null);
        return;
      }
      if (type === 'pudo') {
        const pudoId = `pudo-${v4()}`;
        const targetGroupId =
          pudoCustomId.trim() !== '' ? pudoCustomId.trim() : `pudo-group-${v4()}`;
        dispatch(
          addPudoAction({
            group: { id: targetGroupId },
            pudo: {
              id: pudoId,
              groupId: targetGroupId,
              lat: position.lat,
              lng: position.lng,
              address,
              duration: parseInt(pudoDuration, 10) || 0,
              assignAt: pudoAssignAt,
            },
          }),
        );
        const newOrders = [
          ...orders,
          {
            ...emptyOrder,
            Address: address,
            Latitude: position.lat,
            Longitude: position.lng,
            Activity: 'pudo',
            InternalID: pudoId,
            ID: targetGroupId,
            Group: targetGroupId,
            Position: pudoAssignAt,
            TimeCreated: Date.now(),
            Name: `${pudoId} - pudo`,
            Demand: undefined,
          },
        ];
        handleSetTourParameter({ ordersMode: 'manual' });
        handleSelectTourById({ selectedTour: display.selectedTour, noMapMovement: true });
        handleSetOrders(newOrders);
        if (!user.mapSettings?.hideGroups) {
          handleSetUserParam({
            mapSettings: { ...user.mapSettings, hideGroups: true, groupsChange: true },
          });
        }
        handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
        handleRecordUsage({ event: USAGE_EVENTS.ORDERS_ADD, mode: 'manual' });
        setPosition(null);
        return;
      }
      if (type === 'reload') {
        if (reloadLimitReached) return;
        const existing = reloads;
        dispatch(
          addReload({
            id: `reload-${v4()}`,
            lat: position.lat,
            lng: position.lng,
            address,
            duration: parseInt(reloadDurationValue, 10) || 0,
          }),
        );
        if (existing.length === 0) {
          const currentCapacity = tourPlanner.vehicles[0]?.capacity;
          if (Array.isArray(currentCapacity) && currentCapacity.length > 0) {
            const totalDemand = calcTotalDemand(orders);
            const reloadCapacity = calcInitialReloadCapacity(currentCapacity, totalDemand);
            const updatedVehicles = applyCapacityToFirstVehicle(
              tourPlanner.vehicles,
              reloadCapacity,
            );
            handleSetTourParameter({ vehicles: updatedVehicles, index: solution.show });
          }
        }
        handleSelectTourById({ selectedTour: display.selectedTour, noMapMovement: true });
        handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
        setPosition(null);
        return;
      }
      if (type === 'parking') {
        const parkingId = `parking-${v4()}`;
        dispatch(
          addParking({
            id: parkingId,
            lat: position.lat,
            lng: position.lng,
            address,
            duration: parseInt(parkingDurationValue, 10) || 0,
          }),
        );
        // Auto-attach the new parking spot to the nearest order so the solver's
        // stopConfiguration/parking configuration is actually referenced by a job
        // (see parkingIds on job places), instead of being defined but unused.
        const nearestOrder = findNearestOrder(orders, position.lat, position.lng);
        if (nearestOrder) {
          const orderIdx = orders.indexOf(nearestOrder);
          dispatch(updateOrder({ ...nearestOrder, ParkingId: parkingId }, solution.show, orderIdx));
        }
        handleSelectTourById({ selectedTour: display.selectedTour, noMapMovement: true });
        handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
        setPosition(null);
        return;
      }

      handleSetUserParam({ jsonPanelOpen: true });
      const name =
        tourPlanner.editedOrder?.Name?.length > 0 ? tourPlanner.editedOrder.Name : emptyOrder.Name;
      const demand = tourPlanner.editedOrder?.Demand || [1];
      const newOrders = [
        ...orders,
        {
          ...emptyOrder,
          Address: address,
          Latitude: position.lat,
          Longitude: position.lng,
          Activity: type,
          InternalID: id,
          ID: id,
          TimeCreated: Date.now(),
          Demand: demand,
          Name: name,
        },
      ];
      handleSetTourParameter({ ordersMode: 'manual' });
      handleSelectTourById({ selectedTour: display.selectedTour, noMapMovement: true });

      if (reloads.length > 0) {
        const currentCapacity = tourPlanner.vehicles[0]?.capacity;
        if (Array.isArray(currentCapacity) && currentCapacity.length > 0) {
          const totalDemand = calcTotalDemand(newOrders);
          const numReloads = reloads.length;
          const reloadCapacity = calcReloadCapacity(currentCapacity, totalDemand, numReloads);
          const updatedVehicles = applyCapacityToFirstVehicle(tourPlanner.vehicles, reloadCapacity);
          handleSetTourParameter({ vehicles: updatedVehicles, index: solution.show });
        }
      }

      handleSetOrders(newOrders);
      handleSetSolutionJson({ jsonTriggerVRP: true, jsonNoMapMovement: true });
      handleRecordUsage({ event: USAGE_EVENTS.ORDERS_ADD, mode: 'manual' });
      setPosition(null);
    },
    [
      position,
      handleSetOrders,
      handleSetSolutionJson,
      orders,
      address,
      tourPlanner,
      handleSetUserParam,
      display,
      handleSelectTourById,
      handleRecordUsage,
      handleSetTourParameter,
      solution.show,
      breakDurationInput,
      breakLimitReached,
      pudoDuration,
      pudoAssignAt,
      pudoCustomId,
      reloadDurationValue,
      parkingDurationValue,
      user,
    ],
  );

  useEffect(() => {
    if (!map || !allowRightClick) return undefined;

    const handleContextMenu = (evt) => {
      let coords;
      let screenX;
      let screenY;
      if (evt.currentPointer) {
        screenX = evt.currentPointer.viewportX;
        screenY = evt.currentPointer.viewportY;
        coords = map.screenToGeo(screenX, screenY);
      } else if (evt.viewportX !== undefined && evt.viewportY !== undefined) {
        screenX = evt.viewportX;
        screenY = evt.viewportY;
        coords = map.screenToGeo(screenX, screenY);
      } else if (evt.originalEvent) {
        const rect = map.getElement().getBoundingClientRect();
        screenX = evt.originalEvent.clientX - rect.left;
        screenY = evt.originalEvent.clientY - rect.top;
        coords = map.screenToGeo(screenX, screenY);
      } else {
        console.error('Unable to get coordinates from contextmenu event', evt);
        return;
      }

      if (evt.originalEvent && evt.originalEvent.preventDefault) {
        evt.originalEvent.preventDefault();
      }

      setBoxOrders(null);
      setVisible(true);
      setPanePosition({ x: screenX, y: screenY });

      getLocationAddress(coords, oAuthRef.current, userRef.current).then((location) => {
        setPosition(coords);
        setAddress(location.title);
      });
    };

    map.addEventListener('contextmenu', handleContextMenu);

    return () => {
      map.removeEventListener('contextmenu', handleContextMenu);
      setPosition(null);
      setAddress('');
    };
  }, [map, allowRightClick]);

  useEffect(() => {
    if (!position) return;
    setPudoCustomId('');
  }, [position]);

  function getFeaturesInsideBounds(geoJson, northEast, southWest) {
    const neLat = northEast.lat;
    const neLon = northEast.lng;
    const swLat = southWest.lat;
    const swLon = southWest.lng;

    return geoJson.geo.features
      .filter((feature) => {
        const [lon, lat] = feature.geometry.coordinates;
        return lat >= swLat && lat <= neLat && lon >= swLon && lon <= neLon;
      })
      .map((feature) => feature.properties.id);
  }

  useEffect(() => {
    if (zoomUnassigned && zoomUnassigned.lat && zoomUnassigned.lng && map) {
      map.setCenter({ lat: zoomUnassigned.lat, lng: zoomUnassigned.lng });
      map.setZoom(16);
    }
  }, [zoomUnassigned, map]);

  useEffect(() => {
    if (!map) return undefined;

    const mapElement = map.getElement();
    let startPoint = null;
    let startGeo = null;
    let rectangle = null;
    let isDrawing = false;

    const onMouseMove = (e) => {
      if (!isDrawing || !startPoint || !rectangle) return;

      const rect = mapElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const currentGeo = map.screenToGeo(x, y);

      const topLat = Math.max(startGeo.lat, currentGeo.lat);
      const bottomLat = Math.min(startGeo.lat, currentGeo.lat);
      const leftLng = Math.min(startGeo.lng, currentGeo.lng);
      const rightLng = Math.max(startGeo.lng, currentGeo.lng);

      const lineString = new H.geo.LineString();
      lineString.pushLatLngAlt(topLat, leftLng);
      lineString.pushLatLngAlt(topLat, rightLng);
      lineString.pushLatLngAlt(bottomLat, rightLng);
      lineString.pushLatLngAlt(bottomLat, leftLng);
      lineString.pushLatLngAlt(topLat, leftLng);

      const polygon = new H.geo.Polygon(lineString);
      rectangle.setGeometry(polygon);
    };

    const onMouseUp = () => {
      if (!isDrawing || !rectangle) return;
      isDrawing = false;

      const bounds = rectangle.getBoundingBox();
      const northEast = { lat: bounds.getTop(), lng: bounds.getRight() };
      const southWest = { lat: bounds.getBottom(), lng: bounds.getLeft() };

      if (mapData) {
        const centerLat = (northEast.lat + southWest.lat) / 2;
        const centerLng = (northEast.lng + southWest.lng) / 2;
        const center = { lat: centerLat, lng: centerLng };

        const orderFeatures = getFeaturesInsideBounds(mapData, northEast, southWest);
        const screenPoint = map.geoToScreen(center);

        setBoxOrders(orderFeatures);
        setPanePosition({ x: screenPoint.x, y: screenPoint.y });

        const matchedOrders = orders.filter((o) => orderFeatures.includes(o.InternalID));

        const positions = matchedOrders.map((o) => o.Position);
        const uniquePositions = [...new Set(positions)];
        const containsNumber = uniquePositions.some((pos) => typeof pos === 'number');

        if (containsNumber && uniquePositions.length === 1) {
          setSelectedPosition(EXPORT_TYPES.ORDER);

          const firstWithValue = matchedOrders.find(
            (o) => typeof o.Position === 'number' || o.PositionValue,
          );
          if (firstWithValue?.PositionValue) {
            setLastValid(String(firstWithValue.PositionValue));
            setOrderNumber(String(firstWithValue.PositionValue));
          } else if (isNumber(firstWithValue?.Position)) {
            setLastValid(String(firstWithValue.Position));
            setOrderNumber(String(firstWithValue.Position));
          } else {
            setOrderNumber('');
          }
        } else if (uniquePositions.length === 1 && !uniquePositions.includes(undefined)) {
          setSelectedPosition(uniquePositions[0]);
          setOrderNumber('');
        } else if (uniquePositions.length > 1) {
          setSelectedPosition(EXPORT_TYPES.ANY);
          setOrderNumber('');
        } else {
          setSelectedPosition(EXPORT_TYPES.NONE);
          setOrderNumber('');
        }

        const groups = matchedOrders.map((o) => o.Group).filter(Boolean);
        const allHaveOneGroup =
          groups.length === matchedOrders.length && new Set(groups).size === 1;
        setGroupName(allHaveOneGroup ? groups[0] : '');

        const maxTime = matchedOrders.map((o) => o.MaxTime).filter(Boolean);
        const allHaveOneMaxTime =
          maxTime.length === matchedOrders.length && new Set(maxTime).size === 1;
        setMaxTimeOnVehicle(allHaveOneMaxTime ? maxTime[0] : '');

        const skillsArr = matchedOrders.map((o) => o.Skills).filter(Boolean);
        const flatSkills = skillsArr.flat();
        const allHaveOneSkill =
          flatSkills.length === matchedOrders.length && new Set(flatSkills).size === 1;
        setSkillsName(allHaveOneSkill ? flatSkills[0] : '');

        const categoryArr = matchedOrders.map((o) => o.Category).filter(Boolean);
        const allHaveOneCategory =
          categoryArr.length === matchedOrders.length && new Set(categoryArr).size === 1;
        setCategoryName(allHaveOneCategory ? categoryArr[0] : '');

        const categoryTypeArr = matchedOrders.map((o) => o.CategoryType).filter(Boolean);
        const allHaveOneCategoryType =
          categoryTypeArr.length === matchedOrders.length && new Set(categoryTypeArr).size === 1;
        setselectedCategory(allHaveOneCategoryType ? categoryTypeArr[0] : '');

        const priorityArr = matchedOrders.map((o) => o.Priority).filter(Boolean);
        const allHaveOnePriority =
          priorityArr.length === matchedOrders.length && new Set(priorityArr).size === 1;
        setSelectedPriority(allHaveOnePriority ? priorityArr[0] : '');

        const allTerritories = matchedOrders.map((o) => o.Territories);
        const allHaveOneTerritory =
          allTerritories.every((t) => Array.isArray(t) && t.length === 1 && t[0]) &&
          new Set(allTerritories.map((t) => t[0])).size === 1;

        setTerritoryName(allHaveOneTerritory ? allTerritories[0][0] : '');
      }

      if (rectangle) {
        map.removeObject(rectangle);
        rectangle = null;
      }
      startPoint = null;
      startGeo = null;

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (map._behavior) {
        map._behavior.enable(H.mapevents.Behavior.Feature.PANNING);
      }
    };

    const onMouseDown = (e) => {
      if (e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();

        const rect = mapElement.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        startPoint = { x, y };
        startGeo = map.screenToGeo(x, y);
        isDrawing = true;

        const lineString = new H.geo.LineString();
        lineString.pushLatLngAlt(startGeo.lat, startGeo.lng);
        lineString.pushLatLngAlt(startGeo.lat, startGeo.lng);
        lineString.pushLatLngAlt(startGeo.lat, startGeo.lng);
        lineString.pushLatLngAlt(startGeo.lat, startGeo.lng);
        lineString.pushLatLngAlt(startGeo.lat, startGeo.lng);

        const polygon = new H.geo.Polygon(lineString);
        rectangle = new H.map.Polygon(polygon, {
          style: {
            strokeColor: 'rgba(51, 136, 255, 0.8)',
            lineWidth: 3,
            fillColor: 'rgba(51, 136, 255, 0.2)',
          },
        });
        map.addObject(rectangle);

        if (map._behavior) {
          map._behavior.disable(H.mapevents.Behavior.Feature.PANNING);
        }

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      }
    };

    mapElement.addEventListener('mousedown', onMouseDown);

    return () => {
      mapElement.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (rectangle) {
        map.removeObject(rectangle);
      }
    };
  }, [map, mapData, orders]);

  const handleMouseMove = (e) => {
    if (!dragging || !map) return;
    e.preventDefault();
    const mapContainerRect = map.getElement().getBoundingClientRect();
    setPanePosition({
      x: e.clientX - mapContainerRect.left - dragOffset.x,
      y: e.clientY - mapContainerRect.top - dragOffset.y,
    });
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    const rect = paneRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setDragging(true);
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, dragOffset]);

  useEffect(() => {
    const inputElement = inputRef.current;
    if (selectedPosition === EXPORT_TYPES.ORDER && inputElement && focus) {
      inputElement.focus();
    }
  }, [selectedPosition, focus]);

  useEffect(() => {
    if (recalculateGroups) {
      const lastIteration = last(solution.requests[solution.show].iterations);
      const avoidAreas =
        generateAvoidAreasFromFileProblem(lastIteration.request.json, orders) || [];
      const excludeAreas =
        generateExcludeAreasFromFileProblem(lastIteration.request.json, orders) || [];
      const { groupAreas = [], territories = [] } = generatePolygonsFromOrders(orders);
      dispatch(
        setTerritoriesFromProblemFile({
          groupAreas,
          territories,
          avoidAreas,
          excludeAreas,
          fromUploadedFile: false,
        }),
      );
      setRecalculateGroups(false);
    }
  }, [orders, dispatch, recalculateGroups, solution.show]);

  useEffect(() => {
    if (!orders) return;
    const cleanedOrders = orders.map((item) => {
      const updatedItem = { ...item };
      if (isTerritoriesHidden) delete updatedItem.Territories;
      if (isSkillsHidden) delete updatedItem.Skills;
      if (isCategoryHidden) {
        delete updatedItem.Category;
        delete updatedItem.CategoryType;
      }
      return updatedItem;
    });
    handleSetOrders(uniqBy(cleanedOrders, 'InternalID'));
    handleSetSolutionJson({ jsonTriggerVRP: true });
  }, [isTerritoriesHidden, isSkillsHidden, isCategoryHidden]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsListRef.current && !suggestionsListRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }

      if (
        skillsSuggestionsListRef.current &&
        !skillsSuggestionsListRef.current.contains(event.target)
      ) {
        setShowSkillsSuggestions(false);
      }

      if (paneRef.current && !paneRef.current.contains(event.target)) {
        if (!showSuggestions && !showSkillsSuggestions) {
          setBoxOrders(null);
        }
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions, showSkillsSuggestions]);

  useEffect(() => {
    const territoriesChanged = user.mapSettings?.territories?.userChange;

    if (!orders || !Array.isArray(testTerritories) || !territoriesChanged) return;
    const validTerritoryNames = new Set(testTerritories.map((a) => a.name));
    const cleanedOrders = orders.map((order) => {
      if (!order.Territories) return order;
      const filteredTerritories = order.Territories.filter((t) => validTerritoryNames.has(t));
      if (filteredTerritories.length > 0) {
        return { ...order, Territories: filteredTerritories };
      }
      const { Territories, ...rest } = order;
      return rest;
    });
    if (!isEqual(cleanedOrders, orders)) {
      handleSetOrders(cleanedOrders);
      handleSetSolutionJson({ jsonTriggerVRP: true });
    }
  }, [orders, testTerritories, user.mapSettings?.territories?.userChange]);

  useEffect(() => {
    const skillsChanged = user.mapSettings?.skills?.userChange;

    if (!orders || !Array.isArray(testSkills) || !skillsChanged) return;
    const validSkillNames = new Set(testSkills.map((s) => s.name));
    const cleanedOrders = orders.map((order) => {
      if (!order.Skills) return order;
      const skillsArr = Array.isArray(order.Skills)
        ? order.Skills
        : String(order.Skills)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
      const fillSkills = skillsArr.filter((s) => validSkillNames.has(s));
      if (fillSkills.length > 0) {
        return {
          ...order,
          Skills: fillSkills.length === 1 ? fillSkills[0] : fillSkills,
        };
      }
      const { Skills, ...rest } = order;
      return rest;
    });
    if (!isEqual(cleanedOrders, orders)) {
      handleSetOrders(cleanedOrders);
      handleSetSolutionJson({ jsonTriggerVRP: true });
    }
  }, [orders, testSkills, user.mapSettings?.skills?.userChange]);

  useEffect(() => {
    if (!Array.isArray(boxOrders)) {
      setShowSuggestions(false);
      setShowSkillsSuggestions(false);
    }
  }, [boxOrders]);

  useEffect(() => {
    if (mapTarget && mapTarget.lat && mapTarget.lng && map) {
      map.setCenter({ lat: mapTarget.lat, lng: mapTarget.lng });
      if (mapTarget.zoom) {
        map.setZoom(mapTarget.zoom);
      }
    }
  }, [mapTarget, map]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (showTerritoryList && !event.target.closest('[data-territory-dropdown]')) {
        setShowTerritoryList(false);
      }

      if (showSkillsSuggestions && !event.target.closest('[data-skills-dropdown]')) {
        setShowSkillsSuggestions(false);
      }

      if (
        showSuggestions &&
        suggestionsListRef.current &&
        !suggestionsListRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }

      const groupWrapper = document.querySelector('[data-group-dropdown]');
      if (showGroupList && groupWrapper && !groupWrapper.contains(event.target)) {
        setShowGroupList(false);
      }

      const maxTimeWrapper = document.querySelector('[data-maxTime-dropdown]');
      if (showMaxTimeOnVehicleList && maxTimeWrapper && !maxTimeWrapper.contains(event.target)) {
        setShowMaxTimeOnVehicleList(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    showTerritoryList,
    showSkillsSuggestions,
    showSuggestions,
    showGroupList,
    showMaxTimeOnVehicleList,
  ]);

  if (!map) return null;

  const screenPos = position ? map.geoToScreen(position) : null;

  return position && visible && !isSolution && allowRightClick && screenPos ? (
    <StyledPositionedPane
      ref={paneRef}
      className="location-markers-popup"
      x={panePosition?.x ?? screenPos.x}
      y={panePosition?.y ?? screenPos.y}
      isDraggable
      isDragging={dragging}
    >
      <StyledName title={address} onMouseDown={handleMouseDown}>
        {address}
      </StyledName>
      <StyledList>
        <StyledLink onClick={() => onAddAddress('pickup')} data-test-id="addAddressAsPickup">
          {addPickup}
        </StyledLink>
        <StyledLink onClick={() => onAddAddress('delivery')} data-test-id="addAddressAsDelivery">
          {addDelivery}
        </StyledLink>
        {!breakLimitReached && (
          <>
            <StyledLink onClick={() => onAddAddress('break')} data-test-id="addAddressAsBreak">
              {addBreak}
            </StyledLink>
            <StyledWrapper>
              <StyledInput
                type="number"
                min="1"
                max="480"
                value={breakDurationInput}
                onChange={(e) =>
                  setBreakDurationInput(Math.max(1, parseInt(e.target.value, 10) || 1))
                }
                title="Duration (min)"
                placeholder="Duration (min)"
              />
            </StyledWrapper>
          </>
        )}
        <StyledLink onClick={() => onAddAddress('pudo')} data-test-id="addAddressAsPudo">
          {addPudoLabel}
        </StyledLink>
        <StyledWrapper>
          <div style={{ position: 'relative' }}>
            <StyledInput
              type="text"
              value={pudoCustomId}
              onChange={(e) => {
                setPudoCustomId(e.target.value);
                setShowPudoIdList(true);
              }}
              onFocus={() => setShowPudoIdList(true)}
              onBlur={() => setTimeout(() => setShowPudoIdList(false), 150)}
              placeholder={pudoIdLabel}
            />
            {showPudoIdList && (
              <ul
                role="listbox"
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  top: '2.5rem',
                  left: 0,
                  width: '100%',
                  maxHeight: '8rem',
                  overflowY: 'auto',
                  background: '#fff',
                  border: '1px solid #ccc',
                  zIndex: 1000,
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                }}
              >
                {orders
                  .filter(
                    (o) =>
                      o.Activity === 'pudo' &&
                      (pudoCustomId === '' ||
                        o.Group.toLowerCase().includes(pudoCustomId.toLowerCase())),
                  )
                  .reduce((acc, o) => {
                    if (!acc.some((item) => item.Group === o.Group)) acc.push(o);
                    return acc;
                  }, [])
                  .map((o) => (
                    <li key={o.Group} style={{ padding: 0, margin: 0 }}>
                      <button
                        type="button"
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '0.375rem',
                          cursor: 'pointer',
                          background: '#fff',
                          border: 'none',
                          textAlign: 'left',
                        }}
                        onMouseDown={() => {
                          setPudoCustomId(o.Group);
                          setShowPudoIdList(false);
                        }}
                      >
                        {o.Group}
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </StyledWrapper>
        <StyledWrapper>
          <StyledInput
            type="number"
            min="0"
            value={pudoDuration}
            onChange={(e) => setPudoDuration(e.target.value)}
            title={pudoDurationLabel}
            placeholder={pudoDurationLabel}
          />
        </StyledWrapper>
        <StyledWrapper>
          <StyledSelect value={pudoAssignAt} onChange={(e) => setPudoAssignAt(e.target.value)}>
            <option value="first">{pudoAssignAtFirst}</option>
            <option value="last">{pudoAssignAtLast}</option>
          </StyledSelect>
        </StyledWrapper>
        {!reloadLimitReached && (
          <>
            <StyledLink onClick={() => onAddAddress('reload')} data-test-id="addAddressAsReload">
              {addReloadLabel}
            </StyledLink>
            <StyledWrapper>
              <StyledInput
                type="number"
                min="0"
                value={reloadDurationValue}
                onChange={(e) => setReloadDurationValue(e.target.value)}
                title={reloadDuration}
                placeholder={reloadDuration}
              />
            </StyledWrapper>
          </>
        )}
        <StyledLink onClick={() => onAddAddress('parking')} data-test-id="addAddressAsParking">
          {addParkingLabel}
        </StyledLink>
        <StyledWrapper>
          <StyledInput
            type="number"
            min="0"
            value={parkingDurationValue}
            onChange={(e) => setParkingDurationValue(e.target.value)}
            title={parkingDuration}
            placeholder={parkingDuration}
          />
        </StyledWrapper>
        <StyledLink onClick={onCopyToClipboard}>{copyToClipboard}</StyledLink>
      </StyledList>
      <ToastContainer hideProgressBar closeButton={false} />
    </StyledPositionedPane>
  ) : Array.isArray(boxOrders) && !isSolution ? (
    <StyledPositionedPane
      ref={paneRef}
      className="location-markers-popup"
      x={panePosition?.x ?? 0}
      y={panePosition?.y ?? 0}
      isDragging={dragging}
      isDraggable
    >
      <StyledCloseButton onClick={() => setBoxOrders(null)} data-test-id="closePopup" blackStyle />
      <StyledName title={boxOrders.join('\n')} onMouseDown={handleMouseDown}>
        {boxOrders.join(',')}
      </StyledName>
      <StyledContainer>
        {!isPositionHidden && (
          <>
            <StyledLabel htmlFor="position">{`${selectPosition}:`}</StyledLabel>
            <StyledSelect
              id="position"
              value={selectedPosition}
              onChange={handlePositionChange}
              leftSide
            >
              <option value={EXPORT_TYPES.ANY}>{orderAny}</option>
              <option value={EXPORT_TYPES.FIRST}>{orderFirst}</option>
              <option value={EXPORT_TYPES.LAST}>{orderLast}</option>
              <option value={EXPORT_TYPES.ORDER}>{orderOrdered}</option>
              <option value={EXPORT_TYPES.NONE}>{orderNone}</option>
            </StyledSelect>
            {selectedPosition === 'order' && (
              <StyledInput
                ref={inputRef}
                type="number"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                onBlur={onValueBlur}
                min="1"
                errorObj={orderNumberError}
                placeholder="Enter order"
              />
            )}
          </>
        )}
        {!isGroupHidden && (
          <StyledWrapper>
            <StyledLabel htmlFor="groups">{`${selectGroup}:`}</StyledLabel>
            <StyledTerrWrapper>
              <StyledRelativeWrapper data-group-dropdown>
                <StyledInputWithIcon
                  type="text"
                  value={groupName}
                  onChange={(e) => {
                    setGroupName(e.target.value);
                    setShowGroupList(true);
                  }}
                  placeholder={groupName === '' ? 'None' : 'Enter group...'}
                  onFocus={() => setShowGroupList(true)}
                />
                <StyledIconWrapper>
                  <StyledIcon src={ChevronDownIcon} alt="" width={11} height={11} />
                </StyledIconWrapper>
                {showGroupList && (
                  <StyledDropdownList
                    hasScroll={pudoFilteredGroups.length > DROPDOWN_CONSTANTS.SCROLL_THRESHOLD}
                  >
                    {groupName === '' && (
                      <StyledDropdownItem>
                        <StyledDropdownButton
                          type="button"
                          isHovered={hoveredGroup === ''}
                          onMouseEnter={() => setHoveredGroup('')}
                          onMouseLeave={() => setHoveredGroup(null)}
                          onClick={() => {
                            setGroupName('');
                            setShowGroupList(false);
                          }}
                        >
                          None
                        </StyledDropdownButton>
                      </StyledDropdownItem>
                    )}
                    {pudoFilteredGroups
                      .filter((group) =>
                        groupName === '' || filteredGroups.includes(groupName)
                          ? true
                          : group.toLowerCase().includes(groupName.toLowerCase()),
                      )
                      .map((group) => (
                        <StyledDropdownItem key={group}>
                          <StyledDropdownButton
                            type="button"
                            isHovered={hoveredGroup === group}
                            isSelected={groupName === group}
                            onMouseEnter={() => setHoveredGroup(group)}
                            onMouseLeave={() => setHoveredGroup(null)}
                            onClick={() => {
                              setGroupName(group);
                              setShowGroupList(false);
                            }}
                          >
                            {group}
                          </StyledDropdownButton>
                        </StyledDropdownItem>
                      ))}
                  </StyledDropdownList>
                )}
              </StyledRelativeWrapper>
            </StyledTerrWrapper>
          </StyledWrapper>
        )}
        {!isMaxTimeHidden && (
          <StyledWrapper>
            <StyledLabel htmlFor="maxTimeOnVehicle">{`${selectMaxTime}:`}</StyledLabel>
            <StyledTerrWrapper>
              <StyledRelativeWrapper data-maxTime-dropdown>
                <StyledInputWithIcon
                  type="number"
                  value={maxTimeOnVehicle}
                  onChange={(e) => {
                    setMaxTimeOnVehicle(e.target.value);
                    setShowMaxTimeOnVehicleList(true);
                  }}
                  placeholder={maxTimeOnVehicle === '' ? 'None' : 'Enter max time...'}
                  onFocus={() => setShowMaxTimeOnVehicleList(true)}
                />
                <StyledIconWrapper>
                  <StyledIcon src={ChevronDownIcon} alt="" width={11} height={11} />
                </StyledIconWrapper>
                {showMaxTimeOnVehicleList && (
                  <StyledDropdownList
                    hasScroll={filteredMaxTime.length > DROPDOWN_CONSTANTS.SCROLL_THRESHOLD}
                  >
                    {maxTimeOnVehicle === '' && (
                      <StyledDropdownItem>
                        <StyledDropdownButton
                          type="button"
                          isHovered={hoveredMaxTime === ''}
                          onMouseEnter={() => setHoveredMaxTime('')}
                          onMouseLeave={() => setHoveredMaxTime(null)}
                          onClick={() => {
                            setMaxTimeOnVehicle('');
                            setShowMaxTimeOnVehicleList(false);
                          }}
                        >
                          None
                        </StyledDropdownButton>
                      </StyledDropdownItem>
                    )}
                    {filteredMaxTime
                      .filter((mxtime) =>
                        maxTimeOnVehicle === ''
                          ? true
                          : String(mxtime)
                              .toLowerCase()
                              .includes(String(maxTimeOnVehicle).toLowerCase()),
                      )
                      .map((mxtime) => (
                        <StyledDropdownItem key={mxtime}>
                          <StyledDropdownButton
                            type="button"
                            isHovered={hoveredMaxTime === mxtime}
                            isSelected={maxTimeOnVehicle === mxtime}
                            onMouseEnter={() => setHoveredMaxTime(mxtime)}
                            onMouseLeave={() => setHoveredMaxTime(null)}
                            onClick={() => {
                              setMaxTimeOnVehicle(mxtime);
                              setShowMaxTimeOnVehicleList(false);
                            }}
                          >
                            {mxtime}
                          </StyledDropdownButton>
                        </StyledDropdownItem>
                      ))}
                  </StyledDropdownList>
                )}
              </StyledRelativeWrapper>
            </StyledTerrWrapper>
          </StyledWrapper>
        )}
        {!isTerritoriesHidden && (
          <StyledWrapper>
            <StyledLabel htmlFor="territories">{`${selectTerritory}:`}</StyledLabel>
            <StyledTerrWrapper>
              <StyledRelativeWrapper data-territory-dropdown>
                <StyledTerritorySelector onClick={() => setShowTerritoryList((v) => !v)}>
                  {territoryName === '' ? 'None' : territoryName || terrPlaceholder}
                  <StyledIconWrapper style={{ right: '0.1rem' }}>
                    <StyledIcon src={ChevronDownIcon} alt="" width={11} height={11} />
                  </StyledIconWrapper>
                </StyledTerritorySelector>
                {showTerritoryList && (
                  <StyledDropdownList
                    hasScroll={filteredAreas.length > DROPDOWN_CONSTANTS.SCROLL_THRESHOLD}
                  >
                    <StyledDropdownItem>
                      <StyledDropdownButton
                        type="button"
                        isHovered={hoveredTerritory === ''}
                        isSelected={territoryName === ''}
                        onMouseEnter={() => setHoveredTerritory('')}
                        onMouseLeave={() => setHoveredTerritory(null)}
                        onClick={() => {
                          setTerritoryName('');
                          setShowTerritoryList(false);
                        }}
                      >
                        None
                      </StyledDropdownButton>
                    </StyledDropdownItem>
                    {filteredAreas.map((area) => (
                      <StyledDropdownItem key={area.name}>
                        <StyledDropdownButton
                          type="button"
                          isHovered={hoveredTerritory === area.name}
                          isSelected={territoryName === area.name}
                          onMouseEnter={() => setHoveredTerritory(area.name)}
                          onMouseLeave={() => setHoveredTerritory(null)}
                          onClick={() => {
                            setTerritoryName(area.name);
                            setShowTerritoryList(false);
                          }}
                        >
                          {area.name}
                        </StyledDropdownButton>
                      </StyledDropdownItem>
                    ))}
                  </StyledDropdownList>
                )}
              </StyledRelativeWrapper>
            </StyledTerrWrapper>
          </StyledWrapper>
        )}
        {!isSkillsHidden && (
          <StyledWrapper paddingTop>
            <StyledLabel htmlFor="skills">{`${selectSkills}:`}</StyledLabel>
            <StyledTerrWrapper>
              <StyledRelativeWrapper data-skills-dropdown>
                <StyledTerritorySelector onClick={() => setShowSkillsSuggestions((v) => !v)}>
                  {skillsName === '' ? 'None' : skillsName || skillsPlaceholder}
                  <StyledIconWrapper style={{ right: '-0.3rem' }}>
                    <StyledIcon src={ChevronDownIcon} alt="" width={11} height={11} />
                  </StyledIconWrapper>
                </StyledTerritorySelector>
                {showSkillsSuggestions && (
                  <StyledDropdownList
                    hasScroll={filteredSkills.length > DROPDOWN_CONSTANTS.SCROLL_THRESHOLD}
                  >
                    <StyledDropdownItem>
                      <StyledDropdownButton
                        type="button"
                        isHovered={hoveredSkill === ''}
                        isSelected={skillsName === ''}
                        onMouseEnter={() => setHoveredSkill('')}
                        onMouseLeave={() => setHoveredSkill(null)}
                        onClick={() => {
                          setSkillsName('');
                          setShowSkillsSuggestions(false);
                        }}
                      >
                        None
                      </StyledDropdownButton>
                    </StyledDropdownItem>
                    {filteredSkills.map((skill) => (
                      <StyledDropdownItem key={skill.name}>
                        <StyledDropdownButton
                          type="button"
                          isHovered={hoveredSkill === skill.name}
                          isSelected={skillsName === skill.name}
                          onMouseEnter={() => setHoveredSkill(skill.name)}
                          onMouseLeave={() => setHoveredSkill(null)}
                          onClick={() => {
                            setSkillsName(skill.name);
                            setShowSkillsSuggestions(false);
                          }}
                        >
                          {skill.name}
                        </StyledDropdownButton>
                      </StyledDropdownItem>
                    ))}
                  </StyledDropdownList>
                )}
              </StyledRelativeWrapper>
            </StyledTerrWrapper>
          </StyledWrapper>
        )}
        {!isCategoryHidden && (
          <StyledWrapper paddingTop>
            <StyledLabel htmlFor="category">{`${selectCategory}:`}</StyledLabel>
            <StyledTerrWrapper>
              <StyledRelativeWrapper data-category-dropdown>
                <StyledTerritorySelector onClick={() => setShowSuggestions((v) => !v)}>
                  {categoryName === '' ? 'None' : categoryName || 'Select category'}
                  <StyledIconWrapper style={{ right: '-0.3rem' }}>
                    <StyledIcon src={ChevronDownIcon} alt="" width={11} height={11} />
                  </StyledIconWrapper>
                </StyledTerritorySelector>
                {showSuggestions && (
                  <StyledDropdownList
                    ref={suggestionsListRef}
                    hasScroll={uniqueCategories.length > DROPDOWN_CONSTANTS.SCROLL_THRESHOLD}
                  >
                    <StyledDropdownItem>
                      <StyledDropdownButton
                        type="button"
                        isHovered={hoveredCategory === ''}
                        isSelected={categoryName === ''}
                        onMouseEnter={() => setHoveredCategory('')}
                        onMouseLeave={() => setHoveredCategory(null)}
                        onClick={() => {
                          setCategoryName('');
                          setShowSuggestions(false);
                        }}
                      >
                        None
                      </StyledDropdownButton>
                    </StyledDropdownItem>
                    {uniqueCategories.map((cat) => (
                      <StyledDropdownItem key={cat.name}>
                        <StyledDropdownButton
                          type="button"
                          isHovered={hoveredCategory === cat.name}
                          isSelected={categoryName === cat.name}
                          onMouseEnter={() => setHoveredCategory(cat.name)}
                          onMouseLeave={() => setHoveredCategory(null)}
                          onClick={() => {
                            setCategoryName(cat.name);
                            setShowSuggestions(false);
                          }}
                        >
                          {cat.name}
                        </StyledDropdownButton>
                      </StyledDropdownItem>
                    ))}
                  </StyledDropdownList>
                )}
              </StyledRelativeWrapper>
            </StyledTerrWrapper>
          </StyledWrapper>
        )}
        {!isPriorityHidden && (
          <StyledPriorityWrapper>
            <StyledLabel htmlFor="priority">{`${selectPriority}:`}</StyledLabel>
            <StyledSelect
              id="priority"
              value={selectedPriority}
              onChange={handlePriorityChange}
              marginLeft
            >
              <option value={null}>None</option>
              <option value={1}>{1}</option>
              <option value={2}>{2}</option>
              <option value={3}>{3}</option>
              <option value={4}>{4}</option>
              <option value={5}>{5}</option>
            </StyledSelect>
          </StyledPriorityWrapper>
        )}
        {isPositionHidden &&
        isGroupHidden &&
        isMaxTimeHidden &&
        isTerritoriesHidden &&
        isPriorityHidden &&
        isSkillsHidden &&
        isCategoryHidden ? (
          <StyledDropText>
            <div>{infoEnable}</div>
            <span onClick={onOpenClick}>{infoEnableSettings}</span>
          </StyledDropText>
        ) : (
          <StyledButtonWrapper>
            <Button
              text="Done"
              disabled={false}
              onClick={onSubmitValues}
              dataTestId="doneBoxOrders"
              panelBtn
              locationBtn
            />
          </StyledButtonWrapper>
        )}
      </StyledContainer>
    </StyledPositionedPane>
  ) : null;
};

export default withTranslation(HereLocationSelector);
